import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { clearSession, isAuthenticated, onAuthLost } from '@/shared/utils/session'
import { OptimizedPicture } from '@/shared/components/OptimizedPicture'
import { IMAGE_SPECS } from '@/shared/utils/imageAssets'

const Logo = () => {
  const [imgError, setImgError] = useState(false)

  if (imgError) {
    return (
      <div className="w-16 h-16 bg-telgo-red rounded-lg flex items-center justify-center">
        <span className="text-white font-bold text-xl">T</span>
      </div>
    )
  }

  return (
    <OptimizedPicture
      spec={IMAGE_SPECS.logo}
      alt="TTelGo Logo"
      className="h-full w-full object-contain object-left"
      loading="eager"
      fetchPriority="auto"
      onError={() => setImgError(true)}
    />
  )
}

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [loggedIn, setLoggedIn] = useState(() => isAuthenticated())
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    setLoggedIn(isAuthenticated())
    return onAuthLost(() => setLoggedIn(false))
  }, [location.pathname])

  const isLoggedIn = loggedIn

  const isActive = (path: string) => location.pathname === path

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/download', label: 'Download App' },
    { path: '/help', label: 'Help Center' },
    { path: '/my-esim', label: 'My eSIM' },
    { path: '/about', label: 'About Us' },
  ]

  if (isLoggedIn) {
    navLinks.push({ path: '/profile', label: 'Profile' })
  } else {
    navLinks.push({ path: '/login', label: 'Log In' })
  }

  const handleLogout = () => {
    clearSession()
    setLoggedIn(false)
    navigate('/login')
  }

  return (
    <nav 
      className="sticky top-0 left-0 right-0 z-50 transition-all duration-300 bg-white"
      style={{
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex justify-between items-center h-14">
          {/* Logo — fixed box prevents flex reflow when image loads */}
          <Link
            to="/"
            className="flex shrink-0 items-center h-10 md:h-12 w-[5.7rem] md:w-[6.8rem]"
          >
            <Logo />
          </Link>

          {/* Desktop Navigation - Center */}
          <div className="hidden lg:flex items-center space-x-6 flex-1 justify-center min-w-0">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`whitespace-nowrap px-3 py-1.5 text-base font-medium transition-colors relative z-10 ${
                  isActive(link.path)
                    ? 'text-telgo-red border-b-2 border-telgo-red'
                    : 'hover:text-telgo-red'
                }`}
                style={!isActive(link.path) ? { color: '#181E4B' } : undefined}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right actions — one Shop Plans always; hamburger only below lg (matches center nav) */}
          <div className="flex items-center gap-2 sm:gap-4">
            {isLoggedIn && (
              <button
                onClick={handleLogout}
                className="hidden lg:inline-flex px-3 py-1.5 text-base font-medium text-gray-700 hover:text-telgo-red transition-colors"
              >
                Logout
              </button>
            )}
            <Link
              to="/shop"
              className="flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 text-sm sm:text-base font-medium text-white bg-telgo-red rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
            >
              <OptimizedPicture
                spec={IMAGE_SPECS.cartIcon}
                alt=""
                className="w-3.5 h-3.5 sm:w-4 sm:h-4 object-contain brightness-0 invert"
                loading="eager"
                width={16}
                height={16}
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                }}
              />
              Shop Plans
            </Link>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 relative z-10"
              aria-label="Toggle menu"
              aria-expanded={isOpen}
            >
              {isOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {isOpen ? (
        <div className="lg:hidden bg-white border-t border-gray-200 relative z-10">
          <div className="px-4 pt-2 pb-4 space-y-2 max-h-[min(70vh,calc(100dvh-3.5rem))] overflow-y-auto overscroll-contain">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  isActive(link.path)
                    ? 'text-telgo-red bg-red-50'
                    : 'hover:bg-gray-50'
                }`}
                style={!isActive(link.path) ? { color: '#181E4B' } : undefined}
              >
                {link.label}
              </Link>
            ))}
            {isLoggedIn && (
              <button
                onClick={() => {
                  setIsOpen(false)
                  handleLogout()
                }}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-gray-50"
                style={{ color: '#181E4B' }}
              >
                Logout
              </button>
            )}
          </div>
        </div>
      ) : null}
    </nav>
  )
}

export default Navbar

